<script setup>
import { computed, reactive } from 'vue';

import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

// Turns an unanswered question into a keyword answer. AI rephrase is always on
// (the server sets it), so the client only writes the facts and the keywords.
defineProps({
  question: {
    type: String,
    required: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);

const draft = reactive({ keywords: [], answer: '' });

const canSubmit = computed(
  () => draft.keywords.length > 0 && !!draft.answer.trim()
);

const submit = () => {
  if (!canSubmit.value) return;
  emit('save', { keywords: draft.keywords, answer: draft.answer.trim() });
};
</script>

<template>
  <form
    class="flex flex-col gap-4"
    data-testid="convert-form"
    @submit.prevent="submit"
  >
    <div class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AI_AGENT.UNANSWERED.FORM.QUESTION') }}
      </span>
      <p
        class="px-3 py-2 text-body-main rounded-lg bg-n-alpha-1 text-n-slate-12"
      >
        {{ question }}
      </p>
    </div>
    <div class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AI_AGENT.UNANSWERED.FORM.KEYWORDS') }}
      </span>
      <TagInput
        v-model="draft.keywords"
        :placeholder="$t('AI_AGENT.UNANSWERED.FORM.KEYWORDS_PLACEHOLDER')"
      />
      <p class="text-xs text-n-slate-10">
        {{ $t('AI_AGENT.UNANSWERED.FORM.KEYWORDS_HELP') }}
      </p>
    </div>
    <div class="flex flex-col gap-1" data-testid="convert-answer">
      <TextArea
        v-model="draft.answer"
        :label="$t('AI_AGENT.UNANSWERED.FORM.ANSWER')"
        :placeholder="$t('AI_AGENT.UNANSWERED.FORM.ANSWER_PLACEHOLDER')"
      />
      <p class="flex items-center gap-1.5 text-xs text-n-slate-10">
        <span class="i-lucide-sparkles size-3.5 shrink-0" />
        {{ $t('AI_AGENT.UNANSWERED.FORM.REPHRASE_NOTE') }}
      </p>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        variant="faded"
        slate
        :label="$t('AI_AGENT.UNANSWERED.FORM.CANCEL')"
        data-testid="convert-cancel"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        :label="$t('AI_AGENT.UNANSWERED.FORM.SAVE')"
        :is-loading="isSaving"
        :disabled="!canSubmit"
        data-testid="convert-submit"
      />
    </div>
  </form>
</template>
